import { useEffect, useRef } from "react";
import { CheckCircle, X } from "lucide-react";

export interface TSuccessToastProps {
    message: string;
    onDismiss: () => void;
    /** Auto-dismiss delay in milliseconds. */
    autoDismissMs?: number;
}

/**
 * Success confirmation (e.g. "File uploaded successfully."), centred and lifted off the
 * bottom edge so it is noticed rather than lost in a corner.
 * Announced politely to screen readers and dismissed automatically.
 */
export function SuccessToast({ message, onDismiss, autoDismissMs = 5000 }: TSuccessToastProps) {
    // Held in a ref so a parent re-render passing a fresh callback does not
    // restart the auto-dismiss timer.
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;

    useEffect(() => {
        const timer = setTimeout(() => onDismissRef.current(), autoDismissMs);
        return () => clearTimeout(timer);
    }, [message, autoDismissMs]);

    return (
        <div
            role="status"
            aria-live="polite"
            className="fixed bottom-24 left-1/2 z-50 flex w-max max-w-md -translate-x-1/2 items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-lg"
        >
            <CheckCircle className="h-5 w-5 flex-shrink-0 text-emerald-600" />
            <span className="flex-1 font-medium">{message}</span>
            <button
                type="button"
                onClick={onDismiss}
                className="rounded p-1 text-emerald-600 hover:bg-emerald-100"
                aria-label="Dismiss notification"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}
